import {Badge, message, Spin, Tabs} from 'antd'
import Title from 'antd/es/typography/Title'
import * as React from 'react'
import {useEffect, useState} from 'react'
import {useHistory} from 'react-router'

import {SiteBean, SiteHealth, SiteInfoBean} from 'api/base/api'
import {siteApi} from 'api/siteApi'

import {CONFIG_PATH, SITES_LIST_PATH} from 'constants/routes'

import {Error} from 'components/globalError'

import {AnalyticsSetup} from 'pages/site/AnalyticsSetup'
import {SiteForm} from 'pages/site/SiteForm.tsx'

import 'pages/site/sitePage.scss'

const { HealthStateEnum } = SiteHealth

const { TabPane } = Tabs

interface Props {
  id?: number
  activeKey?: string
}

export const SitePage: React.FC<Props> = ({ id, activeKey }) => {
  const [siteInfoBean, setSiteInfoBean] = useState<SiteInfoBean>({} as SiteInfoBean)
  const hasTraffic = siteInfoBean?.siteHealth?.healthState !== HealthStateEnum.NotStarted
  const trafficMoreThenOneHourAgo = (siteInfoBean?.siteHealth?.trafficHoursAgo &&
    siteInfoBean?.siteHealth?.trafficHoursAgo > 0) as boolean
  const [isLoading, setIsLoading] = useState(false)
  const [showError, setShowError] = useState(false)

  const [errorMessage, setErrorMessage] = useState(
    `Site Load Error. 
        Please refresh the page or try again later. 
        If the problem persists, please contact Customer Support.`,
  )

  const formTab = (id: number) => (id ? 'Edit' : 'Create')

  const badgeStatus = hasTraffic ? (trafficMoreThenOneHourAgo ? 'default' : 'success') : 'error'

  const analyticsSetupTab = (id: number) =>
    !id ? (
      'Analytics Setup'
    ) : (
      <>
        <span style={{ paddingRight: '.5em' }}>Analytics Setup</span>
        <Badge status={badgeStatus} />
      </>
    )

  const loadSiteBean = (id: number) => {
    setIsLoading(true)
    siteApi.getSite(id).then(
      (response) => {
        setSiteInfoBean(response)
        setIsLoading(false)
        setShowError(false)
      },
      () => {
        setIsLoading(false)
        setShowError(true)
        setErrorMessage('Site load error')
      },
    )
  }

  const history = useHistory()

  const handleTabClick = (key: string) => {
    // eslint-disable-next-line
    history.push(CONFIG_PATH + '/' + id + '/edit' + `/${key}`)
  }

  const createSite = (form: any, protocol: string) => {
    const createForm = {
      description: form.description ? form.description : '',
      domain: (protocol as any) + form.domain,
      name: form.name,
    } as SiteBean

    siteApi.createSite(createForm).then((resp) => {
      if (resp) {
        history.push(`${CONFIG_PATH}/${resp}/edit/setup`)
      }
      message.success('Site added')
      history.push(SITES_LIST_PATH)
    })
  }

  const updateSite = (form: SiteInfoBean, protocol: string) => {
    siteApi.updateSite(id as number, {...form, domain: protocol + form.domain}).then(
      () => {
        setIsLoading(false)
        message.success('Site changes saved')
        history.push(SITES_LIST_PATH)
      },
      () => {
        setIsLoading(false)
        setShowError(true)
        setErrorMessage(
          `Site update failed. 
                    Please try again. 
                    If the problem persists, please contact Customer Support.`,
        )
      },
    )
  }
  const handleArchive = (id: number) => {
    siteApi
      .archive(id)
      .then(() => {
        message.warning('Site is archived')
      })
      .then(() => history.push(SITES_LIST_PATH))
  }

  useEffect(() => {
    if (id) {
      loadSiteBean(id)
    }
  }, [id, activeKey])

  return (
    <div className="site-page">
      <Error showError={showError} message={errorMessage} closable isAuthorised />
      <div className="site-page__main">
        <Tabs type="card" activeKey={activeKey ?? 'update'} onChange={handleTabClick}>
          <TabPane key="update" tab={formTab(id as number)}>
            <Spin spinning={isLoading}>
              <div className="site-form__title">
                {id ? <Title level={4}>Edit {siteInfoBean?.name}</Title> : <Title level={4}>Add new site</Title>}
              </div>
              {!id ? (
                <SiteForm submitButtonName="Add Site" handleSubmit={createSite} />
              ) : (
                <SiteForm
                  siteInfoBean={siteInfoBean}
                  submitButtonName="Save"
                  showArchiveButton
                  handleSubmit={updateSite}
                  handleArchive={handleArchive}
                />
              )}
            </Spin>
          </TabPane>

          <TabPane key="setup" disabled={!id} tab={analyticsSetupTab(id as number)}>
            <AnalyticsSetup
              siteBean={siteInfoBean}
              hasTraffic={hasTraffic}
              trafficMoreThenOneHourAgo={trafficMoreThenOneHourAgo}
            />
          </TabPane>
        </Tabs>
      </div>
    </div>
  )
}
